import React from "react";
import { motion, AnimatePresence } from "motion/react";
import { Loader2Icon } from "lucide-react";
import { useWriteFileStream } from "@/hooks/use-write-file-stream";
import { imagineStore } from "@/stores/imagine.store";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import { type WorkspaceState as WorkspaceStateType } from "@imagine/realtime/schemas";
import { useDebounce } from "@uidotdev/usehooks";

export const WorkspaceStateSteps = React.memo(function WorkspaceStateSteps({
  steps,
}: {
  steps: WorkspaceStateType["steps"];
}) {
  return (
    <div className="bg-muted rounded-[16px] w-110 px-[3px] pb-[3px] text-sm text-foreground/70 pointer-events-none [box-shadow:0_1px_0_0_rgba(255,255,255,0.08)_inset]">
      <motion.div
        className="flex flex-col h-10 items-center justify-center"
        layout="position"
      >
        Setting up your project
      </motion.div>
      <motion.div
        className="flex flex-col rounded-[14px] border border-muted-foreground/5 overflow-hidden"
        layout
      >
        <AnimatePresence initial={false} mode="popLayout">
          {steps.map((step) => {
            return (
              <motion.div
                key={"step-" + step.id}
                className="flex items-center gap-3 bg-background px-3 first:rounded-t-[13px] last:rounded-b-[13px] not-last:border-b border-b-muted-foreground/10 not-first:[box-shadow:inset_0_1px_0_0_rgba(0,0,0,0.40)]"
                layout
                initial={{
                  opacity: 0,
                  height: 0,
                  y: -20,
                }}
                animate={{
                  opacity: 1,
                  height: 44, // h-11 = 44px
                  y: 0,
                }}
                exit={{
                  opacity: 0,
                  height: 0,
                  y: -10,
                }}
                transition={{
                  layout: { duration: 0.3, ease: [0.32, 0.72, 0, 1] },
                  opacity: { duration: 0.2 },
                  height: { duration: 0.3, ease: [0.32, 0.72, 0, 1] },
                  y: { duration: 0.3, ease: [0.32, 0.72, 0, 1] },
                }}
              >
                <AnimatePresence mode="wait">
                  <motion.div
                    key={step.text}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                    className="flex items-center gap-2"
                  >
                    <div>
                      {step.status === "completed" ? (
                        <CheckCircleIcon className="size-4 text-foreground" />
                      ) : (
                        <Loader2Icon className="size-4 text-foreground/80 animate-spin" />
                      )}
                    </div>
                    <div className="relative overflow-hidden flex-1">
                      {step.text}
                    </div>
                  </motion.div>
                </AnimatePresence>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </motion.div>
    </div>
  );
});

export const WorkspaceState = React.memo(function WorkspaceState() {
  const workspaceStateError = imagineStore.use.workspaceState.error();
  const workspaceStateStepsDebounced = useDebounce(
    imagineStore.use.workspaceState.steps(),
    1000,
  );

  if (workspaceStateError) {
    return <p className="text-destructive">{workspaceStateError}</p>;
  }

  if (workspaceStateStepsDebounced && workspaceStateStepsDebounced.length > 0) {
    return <WorkspaceStateSteps steps={workspaceStateStepsDebounced} />;
  }
});
